<script setup lang="ts">
  import { GripHorizontal, CircleOff, Trash2 } from 'lucide-vue-next'
  import { Switch } from '@/components/ui/switch'
  import { useTranslation } from '@/composables/useTranslation'

  defineProps<{
    questionTitle: string
  }>()

  const { t } = useTranslation({
    en: {
      virtualQuestionDescription:
        'This question is automatically shown to users when this section is repeatable. It cannot be edited.',
      inputType: 'Input type',
      booleanYesNo: 'Boolean (Yes/No)',
      yes: 'Yes',
      no: 'No',
      repeatSection: 'Repeat section',
      goToNextSection: 'Go to next section',
      logicJump: 'Logic jump',
      questionTitle: 'Question title',
      helpText: 'Help text',
      helpTextDescription: 'Add instructions to help clients provide accurate answers'
    },
    de: {
      virtualQuestionDescription:
        'Diese Frage wird den Benutzern automatisch angezeigt, wenn dieser Abschnitt wiederholbar ist. Sie kann nicht bearbeitet werden.',
      inputType: 'Eingabetyp',
      booleanYesNo: 'Boolean (Ja/Nein)',
      yes: 'Ja',
      no: 'Nein',
      repeatSection: 'Abschnitt wiederholen',
      goToNextSection: 'Zum nächsten Abschnitt',
      logicJump: 'Logik-Sprung',
      questionTitle: 'Fragetitel',
      helpText: 'Hilfstext',
      helpTextDescription:
        'Fügen Sie Anweisungen hinzu, um Kunden zu helfen, genaue Antworten zu geben'
    }
  })
</script>

<template>
  <div class="flex w-full flex-col items-start justify-start gap-4 self-stretch">
    <!-- Info message about auto-generated question -->
    <p class="text-sm text-gray-500">
      {{ t('virtualQuestionDescription') }}
    </p>

    <!-- Question Title (read-only) -->
    <div class="flex w-full flex-col items-start justify-start gap-1">
      <label class="text-sm font-medium text-gray-700">
        {{ t('questionTitle') }}
      </label>
      <div class="w-full">
        <div
          class="flex h-10 w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
        >
          <span class="text-sm text-gray-800">{{ questionTitle }}</span>
        </div>
      </div>
    </div>

    <!-- Help Text (read-only, empty)
    <div class="flex w-full flex-col items-start justify-start gap-1">
      <div class="flex flex-col gap-0.5">
        <label class="text-sm font-medium text-gray-700">
          {{ t('helpText') }}
        </label>
        <span class="text-xs text-gray-500">
          {{ t('helpTextDescription') }}
        </span>
      </div>
      <div class="w-full">
        <div
          class="flex h-20 w-full items-start rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
        >
          <span class="text-sm text-gray-400"></span>
        </div>
      </div>
    </div> -->

    <!-- Input Type (read-only display) -->
    <div class="flex flex-col items-start justify-start gap-1 self-stretch">
      <label class="text-sm font-medium text-gray-700">
        {{ t('inputType') }}
      </label>
      <div
        class="flex h-10 w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
      >
        <CircleOff class="h-4 w-4 text-gray-400" />
        <span class="text-sm text-gray-800">{{ t('booleanYesNo') }}</span>
      </div>
    </div>

    <!-- Yes/No Options with Logic Jump (read-only) -->
    <div class="flex flex-col items-start justify-start gap-2 self-stretch">
      <!-- Yes Option -->
      <div class="flex items-center justify-start gap-2 self-stretch">
        <div class="relative h-4 w-4 overflow-hidden">
          <GripHorizontal class="absolute h-4 w-4 text-gray-300" />
        </div>
        <div class="flex-1">
          <div
            class="flex h-10 w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
          >
            <span class="text-sm text-gray-800">{{ t('yes') }}</span>
          </div>
        </div>
        <div class="flex-1">
          <div
            class="flex h-10 w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
          >
            <span class="text-sm text-gray-500">→ {{ t('repeatSection') }}</span>
          </div>
        </div>
        <div
          class="flex w-8 cursor-not-allowed items-center justify-center overflow-hidden rounded-md bg-gray-100 p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
        >
          <Trash2 class="h-4 w-4 text-gray-300" />
        </div>
      </div>

      <!-- No Option -->
      <div class="flex items-center justify-start gap-2 self-stretch">
        <div class="relative h-4 w-4 overflow-hidden">
          <GripHorizontal class="absolute h-4 w-4 text-gray-300" />
        </div>
        <div class="flex-1">
          <div
            class="flex h-10 w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
          >
            <span class="text-sm text-gray-800">{{ t('no') }}</span>
          </div>
        </div>
        <div class="flex-1">
          <div
            class="flex h-10 w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
          >
            <span class="text-sm text-gray-500">→ {{ t('goToNextSection') }}</span>
          </div>
        </div>
        <div
          class="flex w-8 cursor-not-allowed items-center justify-center overflow-hidden rounded-md bg-gray-100 p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
        >
          <Trash2 class="h-4 w-4 text-gray-300" />
        </div>
      </div>

      <!-- Logic Jump indicator -->
      <div class="inline-flex items-center justify-end self-stretch pt-2">
        <div class="flex items-center justify-start gap-2">
          <div class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800">
            {{ t('logicJump') }}
          </div>
          <Switch
            :checked="true"
            disabled
          />
        </div>
      </div>
    </div>
  </div>
</template>
